/**
 * Seller Customer Tickets Service
 * Handles all API calls for seller customer support ticket management
 * Backend: /api/seller/customer-tickets
 */

import api from '../../redux/api';
import i18n from 'i18next';

const API_BASE = '/seller/customer-tickets';

/**
 * Get localized error message
 * @param {string} englishMsg - English message
 * @param {string} arabicMsg - Arabic message
 * @returns {string} Localized message
 */
const getLocalizedError = (englishMsg, arabicMsg) => {
  const currentLang = i18n?.language || 'en';
  return currentLang === 'ar' ? arabicMsg : englishMsg;
};

/**
 * Handle API errors
 * @param {Error} error - The caught error
 * @param {string} operation - The operation that failed
 * @returns {Object} Structured error object
 */
const handleError = (error, operation = 'ticket') => {
  console.error(`[SellerCustomerTicketsService] Error in ${operation}:`, error);

  if (error.success === false || (error.message && error.errors)) {
    return error;
  }

  if (error.response) {
    const status = error.response.status;
    const data = error.response.data;

    if (status === 401) {
      return {
        success: false,
        message: getLocalizedError(
          'Unauthorized access to tickets',
          'غير مصرح بالوصول إلى التذاكر'
        ),
        type: 'AUTH_ERROR'
      };
    }

    if (status === 403) {
      return {
        success: false,
        message: getLocalizedError(
          'Forbidden access to tickets',
          'غير مصرح بالوصول إلى التذاكر'
        ),
        type: 'PERMISSION_ERROR'
      };
    }

    if (status === 404) {
      return {
        success: false,
        message: getLocalizedError(
          'Ticket not found',
          'التذكرة غير موجودة'
        ),
        type: 'NOT_FOUND'
      };
    }

    return {
      success: false,
      message: data?.message || getLocalizedError(
        `Failed to ${operation} ticket`,
        `فشل في ${operation} التذكرة`
      ),
      type: 'API_ERROR'
    };
  }

  return {
    success: false,
    message: getLocalizedError(
      'Network error. Please try again.',
      'خطأ في الشبكة. يرجى المحاولة مرة أخرى.'
    ),
    type: 'NETWORK_ERROR'
  };
};

/**
 * Seller Customer Tickets Service
 */
export const sellerCustomerTicketsService = {
  /**
   * Get ticket translations
   * @returns {Promise<Object>} Translations object
   */
  async getTranslations() {
    try {
      const response = await api.get(`${API_BASE}/translations`);
      return response.data;
    } catch (error) {
      return handleError(error, 'get translations');
    }
  },

  /**
   * Get seller's customer tickets
   * @param {Object} params - Query parameters
   * @param {string} [params.status] - Filter by status
   * @param {string} [params.priority] - Filter by priority
   * @param {string} [params.search] - Search in subject/description
   * @param {number} [params.page=1] - Page number
   * @param {number} [params.limit=20] - Items per page
   * @returns {Promise<Object>} Tickets with pagination
   */
  async getTickets(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.status) queryParams.append('status', params.status);
      if (params.priority) queryParams.append('priority', params.priority);
      if (params.search) queryParams.append('search', params.search);
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);

      const queryString = queryParams.toString();
      const url = queryString ? `${API_BASE}?${queryString}` : API_BASE;
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      return handleError(error, 'fetch');
    }
  },

  /**
   * Get ticket statistics
   * @returns {Promise<Object>} Statistics object
   */
  async getTicketStats() {
    try {
      const response = await api.get(`${API_BASE}/stats`);
      return response.data;
    } catch (error) {
      return handleError(error, 'fetch stats');
    }
  },

  /**
   * Get ticket details
   * @param {number} ticketId - Ticket ID
   * @returns {Promise<Object>} Ticket details
   */
  async getTicketById(ticketId) {
    try {
      const response = await api.get(`${API_BASE}/${ticketId}`);
      return response.data;
    } catch (error) {
      return handleError(error, 'fetch');
    }
  },

  /**
   * Add comment to ticket
   * @param {number} ticketId - Ticket ID
   * @param {Object} data - Comment data
   * @param {string} data.message - Comment text
   * @param {boolean} [data.is_internal] - Internal note
   * @param {Array} [data.attachments] - File attachments
   * @returns {Promise<Object>} Created comment
   */
  async addComment(ticketId, data) {
    try {
      const response = await api.post(`${API_BASE}/${ticketId}/comments`, data);
      return response.data;
    } catch (error) {
      return handleError(error, 'add comment to');
    }
  },

  /**
   * Update ticket status
   * @param {number} ticketId - Ticket ID
   * @param {string} status - New status
   * @returns {Promise<Object>} Updated ticket
   */
  async updateStatus(ticketId, status) {
    try {
      const response = await api.patch(`${API_BASE}/${ticketId}/status`, { status });
      return response.data;
    } catch (error) {
      return handleError(error, 'update status of');
    }
  },

  /**
   * Assign ticket to user
   * @param {number} ticketId - Ticket ID
   * @param {number} assignedTo - User ID to assign
   * @returns {Promise<Object>} Updated ticket
   */
  async assignTicket(ticketId, assignedTo) {
    try {
      const response = await api.patch(`${API_BASE}/${ticketId}/assign`, { assigned_to: assignedTo });
      return response.data;
    } catch (error) {
      return handleError(error, 'assign');
    }
  }
};

export default sellerCustomerTicketsService;
